import { useEffect, useRef, useState } from 'react';
import { CASES, type CaseDef, type ViewId } from '../state';
import { api } from '../lib/api';

type Props = {
    onNavigate: (id: ViewId) => void;
    onPickCase: (c: CaseDef) => void;
};

type FeedTier = 'blue' | 'yellow' | 'green' | 'orange' | 'red';
type FeedItem = { id: number; value: number; tier: FeedTier; who: string };

/**
 * BSP BOXES landing screen. Picks one of three cases, shows live feed and
 * aggregate stats below.
 *
 * The live feed is the real /api/boxes/feed (last 25 wins). Server returns
 * a "color" string aligned with the FeedTier names. We re-poll every 8s so
 * fresh wins show up without forcing a manual refresh.
 */
export function BoxesView({ onNavigate, onPickCase }: Props) {
    const [feed, setFeed] = useState<FeedItem[]>([]);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.boxesFeed();
                if (cancelled) return;
                setFeed(r.items.map((row) => ({
                    id: row.id,
                    value: row.payout,
                    tier: (row.color as FeedTier),
                    who: row.username ? '@' + row.username : (row.firstName ?? 'Аноним'),
                })));
            } catch { /* offline — keep current feed */ }
        };
        void load();
        timerRef.current = window.setInterval(load, 8_000);
        return () => {
            cancelled = true;
            if (timerRef.current) window.clearInterval(timerRef.current);
        };
    }, []);

    return (
        <div className="view">
            <h2 className="text-center fw-900" style={{ fontSize: 14, letterSpacing: 1 }}>
                BSP BOXES
            </h2>

            {CASES.map((c) => (
                <div
                    key={c.id}
                    className="case-card"
                    style={{ borderColor: caseColor(c) }}
                    onClick={() => onPickCase(c)}
                    role="button"
                >
                    <div className="case-info">
                        <span className="case-name" style={{ color: caseColor(c) }}>
                            {c.title}
                        </span>
                        <span className="case-cost">ЦЕНА: {c.price} BSP</span>
                    </div>
                    <span className="case-icon">{caseEmoji(c)}</span>
                </div>
            ))}

            <div className="feed-section">
                <div className="feed-title">
                    <span>Последние победы</span>
                    <span className="live-dot">● LIVE</span>
                </div>
                <div className="feed-track">
                    {feed.length === 0 && (
                        <span className="text-muted f-12" style={{ margin: '0 8px' }}>
                            пока пусто — будь первым
                        </span>
                    )}
                    {feed.map((it) => (
                        <div
                            key={it.id}
                            className={`feed-item tier-${it.tier}`}
                            title={`${it.who} · ${it.value} BSP`}
                        >
                            {it.value}
                        </div>
                    ))}
                </div>
            </div>

            <button type="button" className="back-btn" onClick={() => onNavigate('games')}>
                НАЗАД В ИГРЫ
            </button>
        </div>
    );
}

function caseColor(c: CaseDef) {
    return c.color === 'green' ? 'var(--green)' : c.color === 'blue' ? 'var(--blue)' : 'var(--red)';
}
function caseEmoji(c: CaseDef) {
    return c.color === 'green' ? '🌱' : c.color === 'blue' ? '💎' : '🔥';
}
